/**
 * ReviewPage — Spaced repetition study mode.
 *
 * Shows questions due for review, lets the user rate their recall quality
 * (SM-2 scale 0-5), and displays review statistics.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getDueQuestions, submitReview, getReviewStats } from '../api/review';
import type { ReviewQuestion, ReviewStats } from '../api/review';
import { AppShell } from '../components/ui';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Button, Card } from '../components/ui';
import MarkdownContent from '../components/MarkdownContent';

/* ── Quality rating definition (labels are translated at render time) ────── */

const qualityDefs = [
  { value: 0, key: 'blackout', color: 'bg-red-600' },
  { value: 1, key: 'wrong', color: 'bg-red-500' },
  { value: 2, key: 'hardFail', color: 'bg-orange-500' },
  { value: 3, key: 'hardPass', color: 'bg-yellow-500' },
  { value: 4, key: 'good', color: 'bg-emerald-500' },
  { value: 5, key: 'perfect', color: 'bg-emerald-600' },
] as const;

/* ── Icons ────────────────────────────────────────────────────────────────── */

function BrainIcon() {
  return (
    <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  );
}

/* ── Stats Card ───────────────────────────────────────────────────────────── */

function StatsCard({ stats }: { stats: ReviewStats }) {
  const { t } = useTranslation();
  return (
    <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-4" role="region" aria-label={t('study.review.statsRegion')}>
      <Card className="p-4 text-center">
        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400" aria-label={t('study.review.dueNowAria', { count: stats.due_now })}>{stats.due_now}</p>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t('study.review.dueNow')}</p>
      </Card>
      <Card className="p-4 text-center">
        <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total_cards}</p>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t('study.review.totalCards')}</p>
      </Card>
      <Card className="p-4 text-center">
        <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{stats.mastered}</p>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t('study.review.mastered')}</p>
      </Card>
      <Card className="p-4 text-center">
        <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stats.learning}</p>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t('study.review.learning')}</p>
      </Card>
    </div>
  );
}

/* ── Review Card ──────────────────────────────────────────────────────────── */

function ReviewCard({
  question,
  onRate,
  isSubmitting,
}: {
  question: ReviewQuestion;
  onRate: (quality: number) => void;
  isSubmitting: boolean;
}) {
  const { t } = useTranslation();
  const [showAnswer, setShowAnswer] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);

  const isCorrect = selectedAnswer === question.correct_answer;
  const hasAnswered = selectedAnswer !== null;

  const handleSelectAnswer = (letter: string) => {
    if (hasAnswered) return;
    setSelectedAnswer(letter);
    setShowAnswer(true);
  };

  return (
    <Card className="p-6 md:p-8" role="region" aria-label={t('study.review.reviewCardLabel', { topic: question.topic ?? t('study.review.topicFallback') })}>
      {/* Question header */}
      <div className="mb-4 flex items-center gap-2">
        {question.topic && (
          <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-500/15 dark:text-blue-300">
            {question.topic}
          </span>
        )}
        <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600 dark:bg-white/5 dark:text-slate-400">
          {question.difficulty}
        </span>
        <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">
          {t('study.review.easeFactorInterval', { ease: question.ease_factor.toFixed(1), interval: question.interval })}
        </span>
      </div>

      {/* Question text */}
      <MarkdownContent
        content={question.question_text}
        className="mb-6 text-lg font-medium leading-relaxed text-slate-900 dark:text-white"
      />

      {/* Options */}
      {question.options.length > 0 && (
        <div className="mb-6 space-y-2" role="radiogroup" aria-label={t('study.review.answerOptionsLabel')}>
          {question.options.map((opt) => {
            const isThisCorrect = opt.letter === question.correct_answer;
            const isThisSelected = opt.letter === selectedAnswer;

            let optClasses =
              'w-full rounded-xl border px-4 py-3 text-left text-sm transition-colors ';

            if (!hasAnswered) {
              optClasses +=
                'cursor-pointer border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-slate-200';
            } else if (isThisCorrect) {
              optClasses +=
                'border-emerald-400 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300';
            } else if (isThisSelected && !isThisCorrect) {
              optClasses +=
                'border-red-400 bg-red-50 text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300';
            } else {
              optClasses +=
                'border-slate-200 bg-white opacity-60 dark:border-white/10 dark:bg-white/5';
            }

            return (
              <button
                key={opt.letter}
                type="button"
                role="radio"
                aria-checked={isThisSelected}
                onClick={() => handleSelectAnswer(opt.letter)}
                disabled={hasAnswered}
                className={optClasses}
              >
                <span className="mr-2 font-semibold">{opt.letter}.</span>
                <MarkdownContent inline content={opt.option_text} />
              </button>
            );
          })}
        </div>
      )}

      {/* Show answer / explanation */}
      {showAnswer && (
        <div className="mb-6" aria-live="polite" role="status">
          <div
            className={`mb-3 rounded-xl border px-4 py-3 text-sm ${
              isCorrect
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300'
                : 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300'
            }`}
          >
            {isCorrect ? t('study.review.correct') : t('study.review.incorrectWithAnswer', { answer: question.correct_answer })}
          </div>
          {question.explanation && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
              <p className="mb-1 font-semibold">{t('study.review.explanation')}</p>
              <MarkdownContent content={question.explanation} />
            </div>
          )}
        </div>
      )}

      {/* Rating buttons — show after answering */}
      {showAnswer && (
        <div role="group" aria-label={t('study.review.rateRecallGroupLabel')}>
          <p className="mb-3 text-sm font-medium text-slate-700 dark:text-slate-300" id="recall-rating-label">
            {t('study.review.rateRecallPrompt')}
          </p>
          <div className="grid grid-cols-3 gap-2 md:grid-cols-6" role="group" aria-labelledby="recall-rating-label">
            {qualityDefs.map(({ value, key, color }) => {
              const label = t(`study.review.quality.${key}`);
              const desc = t(`study.review.quality.${key}Desc`);
              return (
                <button
                  key={value}
                  type="button"
                  aria-label={t('study.review.rateAria', { value, label, desc })}
                  onClick={() => onRate(value)}
                  disabled={isSubmitting}
                  className={`rounded-xl px-2 py-3 text-center text-xs font-semibold text-white shadow-sm transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 disabled:opacity-50 ${color}`}
                  title={desc}
                >
                  <span className="block text-lg">{value}</span>
                  <span className="mt-0.5 block">{label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}

/* ── Empty State ──────────────────────────────────────────────────────────── */

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="py-16 text-center">
      <svg className="mx-auto h-16 w-16 text-slate-300 dark:text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <h3 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
        {t('study.review.allCaughtUpTitle')}
      </h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-slate-500 dark:text-slate-400">
        {t('study.review.allCaughtUpDescription')}
      </p>
    </div>
  );
}

/* ── Main Page ────────────────────────────────────────────────────────────── */

export default function ReviewPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('study.review.documentTitle'));

  const queryClient = useQueryClient();
  const [currentIndex, setCurrentIndex] = useState(0);

  const { data: dueData, isLoading: dueLoading } = useQuery({
    queryKey: ['review', 'due'],
    queryFn: () => getDueQuestions(50),
    staleTime: 30_000,
  });

  const { data: stats } = useQuery({
    queryKey: ['review', 'stats'],
    queryFn: getReviewStats,
    staleTime: 30_000,
  });

  const submitMutation = useMutation({
    mutationFn: ({ questionId, quality }: { questionId: number; quality: number }) =>
      submitReview(questionId, quality),
    onSuccess: () => {
      // Move to next question
      setCurrentIndex((prev) => prev + 1);
      // Invalidate to refresh counts
      queryClient.invalidateQueries({ queryKey: ['review', 'stats'] });
    },
  });

  const questions = dueData?.items ?? [];
  const currentQuestion = questions[currentIndex] ?? null;
  const reviewed = currentIndex;
  const total = questions.length;

  const handleRate = (quality: number) => {
    if (!currentQuestion) return;
    submitMutation.mutate({
      questionId: currentQuestion.question_id,
      quality,
    });
  };

  return (
    <AppShell maxWidth="7xl">
      <div className="mx-auto w-full max-w-3xl">
        {/* Page header */}
        <div className="mb-8 flex items-center gap-3">
          <BrainIcon />
          <div>
            <h1 className="text-2xl font-extrabold tracking-[-0.03em] text-slate-900 dark:text-white">
              {t('study.review.title')}
            </h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {t('study.review.subtitle')}
            </p>
          </div>
        </div>

        {/* Stats */}
        {stats && <StatsCard stats={stats} />}

        {/* Progress bar */}
        {total > 0 && (
          <div className="mb-6">
            <div className="mb-2 flex items-center justify-between text-sm text-slate-600 dark:text-slate-400">
              <span id="review-progress-label">{t('study.review.progress')}</span>
              <span>
                {t('study.review.progressCount', { reviewed, total })}
              </span>
            </div>
            <div
              className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-white/10"
              role="progressbar"
              aria-valuenow={reviewed}
              aria-valuemin={0}
              aria-valuemax={total}
              aria-labelledby="review-progress-label"
            >
              <div
                className="h-full rounded-full bg-blue-600 transition-all duration-300 dark:bg-blue-500"
                style={{ width: `${total > 0 ? (reviewed / total) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}

        {/* Loading state */}
        {dueLoading && (
          <div className="flex items-center justify-center py-16" role="status" aria-label={t('study.review.loadingReviewQuestions')}>
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent dark:border-blue-400 dark:border-t-transparent" aria-hidden="true" />
            <span className="sr-only">{t('study.review.loadingReviewQuestions')}</span>
          </div>
        )}

        {/* Current question or empty state */}
        {!dueLoading && currentQuestion && (
          <ReviewCard
            key={currentQuestion.question_id}
            question={currentQuestion}
            onRate={handleRate}
            isSubmitting={submitMutation.isPending}
          />
        )}

        {!dueLoading && !currentQuestion && reviewed === 0 && <EmptyState />}

        {!dueLoading && !currentQuestion && reviewed > 0 && (
          <div className="py-16 text-center">
            <svg className="mx-auto h-16 w-16 text-emerald-500 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
              {t('study.review.sessionCompleteTitle')}
            </h3>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              {t('study.review.sessionCompleteDescription', { count: reviewed })}
            </p>
            <Button
              type="button"
              variant="primary"
              onClick={() => {
                setCurrentIndex(0);
                queryClient.invalidateQueries({ queryKey: ['review', 'due'] });
              }}
              className="mt-6"
            >
              {t('study.review.startNewSession')}
            </Button>
          </div>
        )}

        {/* Error state */}
        {submitMutation.isError && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400" role="alert">
            {t('study.review.errorSubmit')}
          </div>
        )}
      </div>
    </AppShell>
  );
}
